<template>
  <div class="ig-preview-wrap">
    <div class="ig-preview-label">{{ $t('composer.preview_title') }}</div>
    <div class="ig-phone" :class="'type-' + type">
      <div class="ig-notch"></div>
      <div class="ig-status-bar">
        <span>{{ statusTime }}</span>
        <span class="ig-status-icons">
          <i class="fas fa-signal"></i>
          <i class="fas fa-wifi ml-1"></i>
          <i class="fas fa-battery-full ml-1"></i>
        </span>
      </div>

      <!-- Story -->
      <div v-if="type === 'story'" class="ig-story">
        <div class="ig-story-progress"><span></span></div>
        <div class="ig-story-header">
          <img :src="avatarUrl" class="ig-avatar" alt="" />
          <span class="ig-username">{{ username }}</span>
          <span class="ig-time">{{ $t('composer.preview_now') }}</span>
          <i class="fas fa-ellipsis-h ig-story-more ml-auto"></i>
        </div>
        <div class="ig-story-media">
          <video v-if="mediaSrc && isVideo" :key="mediaSrc" :src="mediaSrc" autoplay muted loop playsinline />
          <img v-else-if="mediaSrc" :key="mediaSrc" :src="mediaSrc" alt="" />
          <div v-else class="ig-placeholder">{{ $t('composer.preview_add_media') }}</div>
        </div>
        <div v-if="displayCaption" class="ig-story-caption">{{ displayCaption }}</div>
        <div class="ig-story-reply">
          <span>{{ $t('composer.preview_reply') }}</span>
          <i class="fas fa-heart"></i>
          <i class="fas fa-paper-plane"></i>
        </div>
      </div>

      <!-- Reel -->
      <div v-else-if="type === 'reel'" class="ig-reel">
        <div class="ig-reel-media">
          <video v-if="mediaSrc && isVideo" :key="mediaSrc" :src="mediaSrc" autoplay muted loop playsinline />
          <img v-else-if="mediaSrc" :key="mediaSrc" :src="mediaSrc" alt="" />
          <div v-else class="ig-placeholder ig-placeholder--dark">{{ $t('composer.preview_add_media') }}</div>
        </div>
        <div class="ig-reel-sidebar">
          <div class="ig-reel-action"><i class="fas fa-heart"></i><span>{{ displayLikes }}</span></div>
          <div class="ig-reel-action"><i class="fas fa-comment"></i><span>{{ displayComments }}</span></div>
          <div class="ig-reel-action"><i class="fas fa-paper-plane"></i></div>
          <div class="ig-reel-action"><i class="fas fa-bookmark"></i></div>
          <div class="ig-reel-disc"><img :src="avatarUrl" alt="" /></div>
        </div>
        <div class="ig-reel-bottom">
          <div class="ig-reel-user">
            <img :src="avatarUrl" class="ig-avatar ig-avatar--sm" alt="" />
            <span class="ig-username">{{ username }}</span>
            <button type="button" class="ig-follow-btn">{{ $t('composer.preview_follow') }}</button>
          </div>
          <div v-if="displayCaption" class="ig-reel-caption">
            <span v-for="(part, idx) in captionParts" :key="idx" :class="partClass(part)">{{ part.value }}</span>
          </div>
          <div class="ig-reel-audio"><i class="fas fa-music mr-1"></i>{{ $t('composer.original_audio') }}</div>
        </div>
      </div>

      <!-- Feed -->
      <div v-else class="ig-feed">
        <div class="ig-feed-header">
          <img :src="avatarUrl" class="ig-avatar" alt="" />
          <div class="ig-feed-meta">
            <div class="ig-username">{{ username }}</div>
            <div class="ig-sub">{{ $t('composer.original_audio') }}</div>
          </div>
          <i class="fas fa-ellipsis-h ig-more"></i>
        </div>
        <div class="ig-feed-media">
          <video v-if="mediaSrc && isVideo" :key="mediaSrc" :src="mediaSrc" autoplay muted loop playsinline />
          <img v-else-if="mediaSrc" :key="mediaSrc" :src="mediaSrc" alt="" />
          <div v-else class="ig-placeholder">{{ $t('composer.preview_add_media') }}</div>
        </div>
        <div class="ig-actions">
          <i class="fas fa-heart"></i>
          <i class="fas fa-comment"></i>
          <i class="fas fa-paper-plane"></i>
          <i class="fas fa-bookmark ml-auto"></i>
        </div>
        <div class="ig-likes">{{ $t('composer.preview_likes') }}</div>
        <div class="ig-caption-block">
          <span class="ig-username">{{ username }}</span>
          <span v-if="displayCaption" class="ig-caption-text">
            <span v-for="(part, idx) in captionParts" :key="idx" :class="partClass(part)">{{ part.value }}</span>
          </span>
          <span v-else class="ig-caption-text ig-caption-text--muted">{{ $t('composer.preview_caption_placeholder') }}</span>
        </div>
        <div v-if="hashtags.length" class="ig-hashtag-row">
          <span v-for="tag in hashtags.slice(0, 5)" :key="tag" class="ig-hashtag-pill">#{{ tag }}</span>
        </div>
        <div class="ig-time-ago">{{ $t('composer.preview_now') }}</div>
      </div>

      <div class="ig-home-bar"></div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    type: { type: String, default: 'feed' },
    username: { type: String, default: 'username' },
    avatarUrl: { type: String, required: true },
    mediaSrc: { type: String, default: '' },
    isVideo: { type: Boolean, default: false },
    caption: { type: String, default: '' },
    hashtags: { type: Array, default: () => [] },
    likesCount: { type: Number, default: null },
    commentsCount: { type: Number, default: null },
  },
  computed: {
    displayLikes() {
      return this.formatCount(this.likesCount)
    },
    displayComments() {
      return this.formatCount(this.commentsCount)
    },
    statusTime() {
      const d = new Date()
      const h = d.getHours()
      const m = String(d.getMinutes()).padStart(2, '0')
      const hour = h % 12 || 12
      return `${hour}:${m}`
    },
    displayCaption() {
      const tags = this.hashtags.map((t) => `#${t.replace(/^#/, '')}`).join(' ')
      return [this.caption, tags].filter(Boolean).join('\n')
    },
    captionParts() {
      return this.parseCaption(this.displayCaption)
    },
  },
  methods: {
    formatCount(n) {
      if (n == null || n === '') return '0'
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
      return String(n)
    },
    parseCaption(text) {
      if (!text) return []
      const parts = []
      const regex = /(#\w+|@\w+)/g
      let last = 0
      let match
      while ((match = regex.exec(text)) !== null) {
        if (match.index > last) {
          parts.push({ type: 'text', value: text.slice(last, match.index) })
        }
        parts.push({
          type: match[0].startsWith('#') ? 'hashtag' : 'mention',
          value: match[0],
        })
        last = match.index + match[0].length
      }
      if (last < text.length) {
        parts.push({ type: 'text', value: text.slice(last) })
      }
      return parts
    },
    partClass(part) {
      if (part.type === 'hashtag' || part.type === 'mention') return 'ig-link'
      return ''
    },
  },
}
</script>

<style scoped>
.ig-preview-wrap {
  position: sticky;
  top: 88px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0 2rem;
}

.ig-preview-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8e8e8e;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.ig-phone {
  width: 300px;
  height: 620px;
  background: #000;
  border-radius: 36px;
  padding: 10px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;
}

.ig-notch {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 86px;
  height: 16px;
  background: #111;
  border-radius: 12px;
  z-index: 8;
}

.ig-status-bar {
  position: absolute;
  top: 14px;
  left: 22px;
  right: 22px;
  z-index: 7;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.ig-status-icons {
  font-size: 9px;
  opacity: 0.95;
}

.ig-home-bar {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 110px;
  height: 4px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 999px;
  z-index: 8;
}

.ig-feed,
.ig-story {
  background: #fff;
  height: 100%;
  border-radius: 28px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: #262626;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.ig-story {
  background: #111;
  color: #fff;
  position: relative;
}

.ig-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  background: #dbdbdb;
  flex-shrink: 0;
}

.ig-avatar--sm {
  width: 28px;
  height: 28px;
}

.ig-username {
  font-weight: 600;
  font-size: 13px;
}

.ig-sub {
  font-size: 11px;
  color: #8e8e8e;
}

.ig-feed-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 42px 12px 10px;
}

.ig-feed-meta {
  flex: 1;
  min-width: 0;
}

.ig-more {
  color: #262626;
  font-size: 14px;
}

.ig-feed-media {
  background: #efefef;
  aspect-ratio: 1 / 1;
  position: relative;
  overflow: hidden;
}

.ig-feed-media img,
.ig-feed-media video,
.ig-story-media img,
.ig-story-media video,
.ig-reel-media img,
.ig-reel-media video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ig-placeholder {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8e8e8e;
  font-size: 13px;
  padding: 1rem;
  text-align: center;
}

.ig-placeholder--dark {
  color: rgba(255, 255, 255, 0.55);
}

.ig-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 12px 4px;
  font-size: 22px;
}

.ig-actions .ml-auto {
  margin-left: auto;
}

.ig-likes {
  padding: 0 12px 4px;
  font-size: 13px;
  font-weight: 600;
}

.ig-caption-block {
  padding: 4px 12px 6px;
  font-size: 13px;
  line-height: 1.4;
}

.ig-caption-text {
  margin-left: 4px;
  white-space: pre-wrap;
  word-break: break-word;
}

.ig-caption-text--muted {
  color: #8e8e8e;
  font-style: italic;
}

.ig-link {
  color: #00376b;
}

.ig-hashtag-row {
  padding: 0 12px 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.ig-hashtag-pill {
  font-size: 12px;
  color: #00376b;
}

.ig-time-ago {
  padding: 0 12px 14px;
  font-size: 10px;
  color: #8e8e8e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ig-story-progress {
  position: absolute;
  top: 38px;
  left: 12px;
  right: 12px;
  height: 2px;
  background: rgba(255, 255, 255, 0.35);
  border-radius: 2px;
  z-index: 3;
}

.ig-story-progress span {
  display: block;
  height: 100%;
  width: 35%;
  background: #fff;
  border-radius: 2px;
}

.ig-story-header {
  position: absolute;
  top: 48px;
  left: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.ig-story-header .ig-avatar {
  border: 2px solid #fff;
}

.ig-story-more {
  font-size: 14px;
  opacity: 0.9;
}

.ig-time {
  font-size: 12px;
  opacity: 0.85;
  margin-left: 4px;
}

.ig-story-media {
  flex: 1;
  background: #222;
}

.ig-story-caption {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 52px;
  font-size: 14px;
  line-height: 1.35;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  max-height: 28%;
  overflow: hidden;
  white-space: pre-wrap;
}

.ig-story-reply {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 18px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 14px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 999px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
}

.ig-story-reply span {
  flex: 1;
}

.ig-reel {
  position: relative;
  height: 100%;
  border-radius: 28px;
  overflow: hidden;
  background: #111;
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.ig-reel-media {
  position: absolute;
  inset: 0;
}

.ig-reel-sidebar {
  position: absolute;
  right: 10px;
  bottom: 90px;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.ig-reel-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 22px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
}

.ig-reel-action span {
  font-size: 11px;
  margin-top: 2px;
}

.ig-reel-disc {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid #fff;
  margin-top: 4px;
}

.ig-reel-disc img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ig-reel-bottom {
  position: absolute;
  left: 12px;
  right: 56px;
  bottom: 28px;
  z-index: 4;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
}

.ig-reel-user {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.ig-follow-btn {
  margin-left: auto;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: transparent;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
}

.ig-reel-caption {
  font-size: 13px;
  line-height: 1.35;
  max-height: 2.7em;
  overflow: hidden;
  white-space: pre-wrap;
  margin-bottom: 6px;
}

.ig-reel-caption .ig-link {
  color: #fff;
  opacity: 0.95;
}

.ig-reel-audio {
  font-size: 12px;
  opacity: 0.9;
}

@media (max-width: 991px) {
  .ig-preview-wrap {
    position: static;
    margin-bottom: 1.5rem;
  }
}
</style>
